import React, { useEffect, useState, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Toolbar,
  Fab,
  LinearProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import ApiMap from "../../utils/ApiMap";
import AuthService from "../../utils/AuthService";
import { groupBy } from "../../utils/Uitils";
import bookmarkedLessonsData from "./bookmarkedlessonconfig.json";

const BorderLinearProgress = (props) => (
  <LinearProgress
    variant="determinate"
    value={props.value}
    sx={{
      height: 10,
      backgroundColor: "#EEEEEE",
      "& .MuiLinearProgress-bar": {
        backgroundColor: "#0ebf15cc",
      },
    }}
  />
);

function DisplayAssignment({ assignments }) {
  const courseNames = groupBy(assignments, "CourseName");
  const navigate = useNavigate();

  return Object.entries(courseNames).map(([courseName, modules], i) => (
    <Box key={i} sx={{ mb: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
        {courseName}
      </Typography>

      {Object.entries(groupBy(modules, "ModuleName")).map(
        ([moduleName, lessons], j) => (
          <Fragment key={j}>
            <Typography
              variant="h6"
              sx={{
                background: "linear-gradient(90deg, #1976d2, #2196f3)",
                color: "#fff",
                py: 1,
                px: 2,
                borderRadius: "8px 8px 0 0",
                fontWeight: 600,
              }}
            >
              Module: {moduleName}
            </Typography>

            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {lessons.map((row) => (
                <Box
                  key={row.Id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #f0f0f0",
                    "&:last-child": {
                      borderBottom: "none",
                    },
                    backgroundColor:
                      row.PercentCompleted === 100 ? "#f9fff9" : "#fff",
                    "&:hover": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  <Box sx={{ flex: 3 }}>
                    <Typography variant="subtitle1" fontWeight="500">
                      {row.LessonName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Due:{" "}
                      {new Date(row.DateCompleted).toLocaleDateString("en-US")}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, textAlign: "center" }}>
                    {row.ContentType === "Game" ? (
                      <BorderLinearProgress value={row.PercentCompleted || 0} />
                    ) : (
                      <Typography variant="body2" fontWeight="bold">
                        {row.GradeObtained || 0}/{row.Score || 0} Pts
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, textAlign: "right" }}>
                    {row.PercentCompleted === 100 ? (
                      <CheckCircleOutlineIcon
                        color="success"
                        fontSize="medium"
                      />
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                          navigate(
                            `/Modules/Course/${row.CourseId}/Module/${row.ModuleId}/Chapter/${row.ChapterId}/Lesson/${row.Id}`,
                            {
                              state: {
                                lessonName: row.LessonName,
                                contentId: row.Id,
                                courseId: row.CourseId,
                                moduleId: row.ModuleId,
                                chapterId: row.ChapterId,
                                courseName: courseName,
                              },
                            }
                          )
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          borderRadius: 2,
                          boxShadow: "none",
                          backgroundColor: "#1976d2",
                          "&:hover": {
                            backgroundColor: "#115293",
                          },
                        }}
                      >
                        Open
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Fragment>
        )
      )}
    </Box>
  ));
}

const stripHtml = (htmlString) =>
  typeof htmlString === "string"
    ? htmlString.replace(/<\/?[^>]+(>|$)/g, "")
    : htmlString;

function DisplayBookmarkedLessons({ lessons }) {
  const navigate = useNavigate();
  const courseGroups = groupBy(lessons, "courseName");

  return Object.entries(courseGroups).map(([courseName, modules], i) => (
    <Box key={i} sx={{ mb: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
        {courseName}
      </Typography>

      {Object.entries(groupBy(modules, "moduleName")).map(
        ([moduleName, lessonList], j) => (
          <Fragment key={j}>
            <Typography
              variant="h6"
              sx={{
                background: "linear-gradient(90deg, #1976d2, #2196f3)",
                color: "#fff",
                py: 1,
                px: 2,
                borderRadius: "8px 8px 0 0",
                fontWeight: 600,
              }}
            >
              Module: {moduleName}
            </Typography>

            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {[...lessonList]
                .sort((a, b) => a.id - b.id)
                .map((row) => (
                  <Box
                    key={row.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.5,
                      borderBottom: "1px solid #f0f0f0",
                      "&:last-child": {
                        borderBottom: "none",
                      },
                      backgroundColor: "#fff",
                      "&:hover": {
                        backgroundColor: "#f9f9f9",
                      },
                    }}
                  >
                    <Box sx={{ flex: 3 }}>
                      <Typography variant="subtitle1" fontWeight="500">
                        {stripHtml(row.name)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.description || "No description available."}
                      </Typography>
                      <Typography variant="body2">
                        Duration: {row.duration} min
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Note: {row.userNote || ""}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, textAlign: "right" }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                          navigate(
                            `/Modules/Course/${row.courseId}/Module/${row.moduleId}/Chapter/${row.chapterId}/Lesson/${row.id}`,
                            {
                              state: {
                                lessonName: row.name,
                                contentId: row.id,
                                courseId: row.courseId,
                                moduleId: row.moduleId,
                                chapterId: row.chapterId,
                                courseName: row.courseName,
                              },
                            }
                          )
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 500,
                          borderRadius: 2,
                          boxShadow: "none",
                          backgroundColor: "#1976d2",
                          "&:hover": {
                            backgroundColor: "#115293",
                          },
                        }}
                      >
                        Open
                      </Button>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Fragment>
        )
      )}
    </Box>
  ));
}
export default function TasksAndBookmarksPage() {
  const [studentAssignments, setStudentAssignments] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [bookmarkedLessons, setBookmarkedLessons] = useState([]);
  const location = useLocation();
  const { title } = location.state || {};

  useEffect(() => {
    if (title === "bookmarkedlessons") {
      setBookmarkedLessons(bookmarkedLessonsData);
    } else if (title === "assignmentsdue") {
      const fetchData = async () => {
        try {
          const resp = await axios(
            ApiMap.MY_ASSIGNMENTDUE,
            AuthService.getAuthHeader()
          );
          setStudentAssignments(resp.data.MyDueAssignments);
        } catch (error) {
          console.error("Failed to fetch assignments", error);
        }
      };
      fetchData();
    }
  }, [title]);

  useEffect(() => {
    if (title === "bookmarkedlessons") {
      setBookmarkedLessons(bookmarkedLessonsData);
      // Save to localStorage for global access (e.g., AppBar)
      localStorage.setItem(
        "bookmarkedLessons",
        JSON.stringify(bookmarkedLessonsData)
      );
    }
  }, [title]);

  if (
    (title === "assignmentsdue" &&
      studentAssignments &&
      studentAssignments.length === 0) ||
    (title === "bookmarkedlessons" &&
      bookmarkedLessons &&
      bookmarkedLessons.length === 0)
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          minHeight: "200px",
          textAlign: "center",
        }}
      >
        <Typography sx={{ mb: 2 }}>
          {title === "bookmarkedlessons"
            ? "No bookmarked lessons found."
            : "Loading assignments due..."}
        </Typography>
        {title === "assignmentsdue" && (
          <LinearProgress
            color="secondary"
            sx={{ width: "60%", maxWidth: "400px" }}
          />
        )}
      </Box>
    );
  }

  if (
    (title === "assignmentsdue" &&
      (!studentAssignments || studentAssignments.length === 0)) ||
    (title === "bookmarkedlessons" &&
      (!bookmarkedLessons || bookmarkedLessons.length === 0))
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          minHeight: "200px",
          textAlign: "center",
        }}
      >
        <Typography sx={{ mb: 2 }}>
          {title === "bookmarkedlessons"
            ? "No bookmarked lessons found."
            : !studentAssignments
            ? "Loading assignments due..."
            : "🎉 Congratulations! You are all caught up."}
        </Typography>

        {/* Show progress bar only during loading */}
        {title === "assignmentsdue" && !studentAssignments && (
          <LinearProgress
            color="secondary"
            sx={{ width: "60%", maxWidth: "400px" }}
          />
        )}
      </Box>
    );
  }

  const courseOptions =
    studentAssignments && studentAssignments.length > 0
      ? [
          "All",
          ...Array.from(new Set(studentAssignments.map((a) => a.CourseName))),
        ]
      : ["All"];

  const filteredAssignments =
    selectedCourse === "All"
      ? studentAssignments
      : studentAssignments.filter((a) => a.CourseName === selectedCourse);

  const filteredBookmarkedLessons =
    selectedCourse === "All"
      ? bookmarkedLessons
      : bookmarkedLessons.filter((l) => l.courseName === selectedCourse);

  return (
    <Box sx={{ p: 2 }}>
      <Toolbar id="back-to-top-anchor" />

      {/* Course Filter Dropdown */}
      <FormControl sx={{ mb: 3, minWidth: 250 }}>
        <InputLabel id="course-filter-label">Filter by Course</InputLabel>
        <Select
          labelId="course-filter-label"
          value={selectedCourse}
          label="Filter by Course"
          onChange={(e) => setSelectedCourse(e.target.value)}
        >
          {courseOptions.map((course, idx) => (
            <MenuItem key={idx} value={course}>
              {course}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {title === "bookmarkedlessons" ? (
        <DisplayBookmarkedLessons lessons={filteredBookmarkedLessons} />
      ) : (
        <DisplayAssignment assignments={filteredAssignments} />
      )}

      <Fab
        color="secondary"
        size="small"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <KeyboardArrowUpIcon />
      </Fab>
    </Box>
  );
}
